import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';

/**
 * Pipeline Automation ROI Calculator
 * Helps B2B service businesses estimate revenue impact of automating
 * their pipeline, intake, and follow-up processes.
 */
const PipelineRoiCalculatorPage: React.FC = () => {
  const [inputs, setInputs] = useState({
    monthlyLeads: 50,
    currentResponseHours: 24,
    currentFollowUpRate: 30,
    avgDealValue: 3000,
    currentCloseRate: 20,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: Math.max(0, Number(value)) }));
  };

  const results = useMemo(() => {
    const {
      monthlyLeads,
      currentResponseHours,
      currentFollowUpRate,
      avgDealValue,
      currentCloseRate,
    } = inputs;

    // Automation impact assumptions (conservative, research-backed)
    const automatedResponseHours = 0.07; // ~4 minutes
    const automatedFollowUpRate = 85;    // % of leads followed up
    const improvedCloseRate = Math.min(currentCloseRate * 1.5, 50); // 50% lift, capped at 50%

    // Current state
    const currentDealsPerMonth = (monthlyLeads * (currentFollowUpRate / 100) * (currentCloseRate / 100));
    const currentMonthlyRevenue = currentDealsPerMonth * avgDealValue;

    // With automation
    const automatedDealsPerMonth = (monthlyLeads * (automatedFollowUpRate / 100) * (improvedCloseRate / 100));
    const automatedMonthlyRevenue = automatedDealsPerMonth * avgDealValue;

    const revenueUpliftMonthly = automatedMonthlyRevenue - currentMonthlyRevenue;
    const revenueUpliftAnnual = revenueUpliftMonthly * 12;

    // Hours saved on manual follow-up (assume 15 min per manual follow-up attempt)
    const manualFollowUpsPerMonth = monthlyLeads * (currentFollowUpRate / 100);
    const hoursPerFollowUp = 0.25;
    const hoursSavedMonthly = manualFollowUpsPerMonth * hoursPerFollowUp
      + monthlyLeads * 0.1; // intake data entry savings

    // Response time improvement
    const responseTimeImprovement = currentResponseHours > 0
      ? Math.round(((currentResponseHours - automatedResponseHours) / currentResponseHours) * 100)
      : 0;

    return {
      currentDealsPerMonth: Math.round(currentDealsPerMonth * 10) / 10,
      automatedDealsPerMonth: Math.round(automatedDealsPerMonth * 10) / 10,
      currentMonthlyRevenue: Math.round(currentMonthlyRevenue),
      automatedMonthlyRevenue: Math.round(automatedMonthlyRevenue),
      revenueUpliftMonthly: Math.round(revenueUpliftMonthly),
      revenueUpliftAnnual: Math.round(revenueUpliftAnnual),
      hoursSavedMonthly: Math.round(hoursSavedMonthly),
      responseTimeImprovement,
      improvedCloseRate: Math.round(improvedCloseRate),
      automatedFollowUpRate,
    };
  }, [inputs]);

  const formatCurrency = (val: number) =>
    val >= 1000
      ? `£${(val / 1000).toFixed(1)}k`
      : `£${val.toLocaleString()}`;

  const inputFields: {
    name: keyof typeof inputs;
    label: string;
    unit: string;
    min: number;
    max: number;
    step: number;
    hint: string;
  }[] = [
    {
      name: 'monthlyLeads',
      label: 'Monthly Inbound Leads',
      unit: 'leads/mo',
      min: 1,
      max: 1000,
      step: 1,
      hint: 'Total enquiries, form fills, and inbound contacts per month.',
    },
    {
      name: 'currentResponseHours',
      label: 'Current Response Time',
      unit: 'hours',
      min: 0.5,
      max: 168,
      step: 0.5,
      hint: 'How long on average before a lead gets a first response from your team.',
    },
    {
      name: 'currentFollowUpRate',
      label: 'Current Follow-Up Rate',
      unit: '%',
      min: 1,
      max: 100,
      step: 1,
      hint: 'Percentage of leads that actually receive a follow-up from your team.',
    },
    {
      name: 'avgDealValue',
      label: 'Average Deal Value',
      unit: '£',
      min: 100,
      max: 500000,
      step: 100,
      hint: 'Average contract or project value when a deal closes.',
    },
    {
      name: 'currentCloseRate',
      label: 'Current Close Rate',
      unit: '%',
      min: 1,
      max: 80,
      step: 1,
      hint: 'Percentage of followed-up leads that become paying clients.',
    },
  ];

  return (
    <Layout>
      {/* Hero */}
      <div className="bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50"></div>
        <div className="relative py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
                Pipeline Automation ROI Calculator
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-600">
                See how much revenue you could unlock by automating your lead intake and follow-up
              </p>
              <div className="inline-flex items-center bg-blue-50 border border-blue-200 rounded-full px-6 py-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse mr-3"></div>
                <span className="text-blue-700 font-medium">No extra headcount required</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Nav hint */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm shadow-sm">
              <span className="mr-2">🔧</span>
              <span>Want a tailored service recommendation?</span>
              <Link
                to="/tools/service-selector"
                className="ml-3 text-blue-600 hover:text-blue-800 transition-colors font-medium"
              >
                Try our AI Service Selector →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Current Pipeline</h2>
                <p className="text-gray-500 text-sm mb-8">
                  Adjust the sliders to match your current situation.
                </p>

                <div className="space-y-7">
                  {inputFields.map((field) => (
                    <div key={field.name}>
                      <div className="flex justify-between items-baseline mb-2">
                        <label className="text-sm font-semibold text-gray-700">{field.label}</label>
                        <span className="text-blue-600 font-bold text-lg">
                          {field.unit === '£'
                            ? `£${Number(inputs[field.name]).toLocaleString()}`
                            : field.unit === '%'
                            ? `${inputs[field.name]}%`
                            : field.unit === 'hours'
                            ? `${inputs[field.name]}h`
                            : inputs[field.name]}
                        </span>
                      </div>
                      <input
                        type="range"
                        name={field.name}
                        min={field.min}
                        max={field.max}
                        step={field.step}
                        value={inputs[field.name]}
                        onChange={handleChange}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <p className="text-xs text-gray-400 mt-1">{field.hint}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="flex flex-col gap-6">
                {/* Revenue uplift headline */}
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-2xl shadow-lg p-8 text-center">
                  <p className="text-blue-100 font-medium mb-1 text-sm uppercase tracking-wide">
                    Estimated Additional Annual Revenue
                  </p>
                  <p className="text-5xl md:text-6xl font-extrabold mb-2">
                    {formatCurrency(results.revenueUpliftAnnual)}
                  </p>
                  <p className="text-blue-200 text-sm">
                    +{formatCurrency(results.revenueUpliftMonthly)} per month
                  </p>
                </div>

                {/* Metric cards */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    {
                      label: 'Hours Saved / Month',
                      value: `${results.hoursSavedMonthly}h`,
                      sub: 'freed from manual follow-up',
                      color: 'text-green-600',
                      bg: 'bg-green-50',
                    },
                    {
                      label: 'Response Time Reduction',
                      value: `${results.responseTimeImprovement}%`,
                      sub: `down to ~4 min`,
                      color: 'text-purple-600',
                      bg: 'bg-purple-50',
                    },
                    {
                      label: 'Deals / Month (now)',
                      value: `${results.currentDealsPerMonth}`,
                      sub: `${inputs.currentCloseRate}% close rate`,
                      color: 'text-gray-600',
                      bg: 'bg-gray-50',
                    },
                    {
                      label: 'Deals / Month (with AI)',
                      value: `${results.automatedDealsPerMonth}`,
                      sub: `${results.improvedCloseRate}% close rate`,
                      color: 'text-blue-600',
                      bg: 'bg-blue-50',
                    },
                  ].map((card) => (
                    <div key={card.label} className={`${card.bg} rounded-xl p-5`}>
                      <p className="text-xs text-gray-500 font-medium mb-1">{card.label}</p>
                      <p className={`text-2xl font-extrabold ${card.color}`}>{card.value}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center shadow-sm">
                  <p className="text-gray-700 font-semibold mb-1">Ready to capture this revenue?</p>
                  <p className="text-gray-500 text-sm mb-5">
                    Book a free pipeline audit — we'll show you exactly where automation closes your gaps.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-colors"
                  >
                    Book a Free Pipeline Audit
                  </Link>
                  <Link
                    to="/services/crm-pipeline-automation"
                    className="inline-block mt-3 text-blue-600 text-sm font-medium hover:underline"
                  >
                    Learn about CRM &amp; Pipeline Automation →
                  </Link>
                </div>
              </div>
            </div>

            {/* Assumptions disclaimer */}
            <p className="text-center text-xs text-gray-400 mt-8">
              Estimates are based on industry benchmarks: 50% close-rate lift from faster response, 85% follow-up coverage with automation.
              Actual results will vary. Book a free audit for a personalised projection.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
              How Does Pipeline Automation Actually Work?
            </h2>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg shadow-sm overflow-hidden">
                  <details className="group">
                    <summary className="cursor-pointer p-6 font-medium text-lg text-gray-800 hover:bg-gray-100 transition-colors focus:outline-none flex items-center justify-between">
                      <span>{faq.question}</span>
                      <div className="ml-4 transform group-open:rotate-180 transition-transform">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-gray-600 leading-relaxed">{faq.answer}</div>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const faqData = [
  {
    question: 'How does pipeline automation improve close rates?',
    answer:
      'Speed-to-lead is the single biggest driver of close rate. Studies show that responding within 5 minutes vs 30 minutes makes you 21x more likely to qualify a lead. Add consistent follow-up sequences and you eliminate the 44% of reps who give up after one follow-up — capturing deals your competitors miss.',
  },
  {
    question: 'Will this replace my sales team?',
    answer:
      'No. Automation handles the repetitive, time-sensitive tasks: capturing leads, sending the first response, logging CRM data, and nurturing with follow-up sequences. Your sales team focuses on what humans do best — building relationships and closing. Think of it as giving every rep an AI assistant that never sleeps.',
  },
  {
    question: 'How long does it take to set up?',
    answer:
      'A core intake and follow-up automation can typically be live within 2–3 weeks. More complex pipeline workflows with custom CRM integrations usually take 4–6 weeks. We start with a free pipeline audit to map your current process and give you a precise timeline.',
  },
  {
    question: 'Which CRMs do you work with?',
    answer:
      'We integrate with HubSpot, Salesforce, Pipedrive, Zoho CRM, and most other platforms. We also support any CRM with a Zapier or Make (formerly Integromat) connector, or a direct REST API. If you have a custom CRM, ask us — we can usually build a native integration.',
  },
  {
    question: 'How accurate is this calculator?',
    answer:
      'The estimates are based on conservative industry benchmarks. A 50% close-rate improvement from faster response is well-supported by sales research, and 85% follow-up coverage is achievable with basic automation. Actual results depend on your market, offer, and team — which is why we offer a free pipeline audit to give you a personalised projection.',
  },
];

export default PipelineRoiCalculatorPage;
